import React, {useEffect, useRef, useState} from 'react';
import './ManagePermissions.scss';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {setAdditionalPrefs} from '../../../../redux/slice/assignmentSlice';

const labels =
  (t('assignment.managePermissions.permissionLabels', {
    returnObjects: true,
    defaultValue: {},
  }) as Record<string, string>) || {};

const REDACTION_KEYS = [
  'isEnableImageRedactions',
  'isEnableAudioRedactions',
  'isEnableNativeRedactions',
];

const categories: Record<string, {left: string[]; right: string[]}> = {
  action: {
    left: [
      'isAllowPopoutPanels',
      'isAllowAccesstoFullDoclist',
      'isAllowManualSort',
      'isAllowFolderActions',
      'isAllowPDFPrinting',
      'isAllowNativeDownloads',
    ],
    right: [
      'isEnableImageRedactions',
      'isEnableAudioRedactions',
      // Native Redaction toggle is hidden. isEnableNativeRedactions is sent
      // with the same value as isEnableImageRedactions.
      // 'isEnableNativeRedactions',
      'isAllowReviwerRemarks',
    ],
  },
  coding: {
    left: [
      'isAllowSaveWithoutCompletion',
      'isAutoSaveCodingForm',
      'isAllowCodingOutsideReviwerBatch',
    ],
    right: ['isAllowCodingStamps', 'shouldCompleteOnApplyCodingStamp'],
  },
  showHide: {
    left: [
      'isShowMiniDocList',
      'isShowAssignmentProgress',
      'isShowDefaultViewTab',
      'isAllowProductionView',
      'isEnableHightlighting',
      'isAllowMetadataView',
      'isExposeDocumentHistory',
    ],
    right: [
      'isShowFoldersTab',
      'isShowAnalyticsPanel',
      'isShowEmailThreadMaptabAnalyticsPanel',
      'isShowNearDuplicatetabAnalyticsPanel',
      'isShowFamilyMemberstabAnalyticsPanel',
      'isShowConceputallySimilartabAnalyticsPanel',
    ],
  },
};

interface IManagePermissionsProps {
  onClose: () => void;
}

const ManagePermissions: React.FC<IManagePermissionsProps> = ({onClose}) => {
  const assignment = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );
  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === assignment.parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;
  const originalRef = useRef<Record<string, any> | null>(null);
  const [localPrefs, setLocalPrefs] = useState<Record<string, boolean>>({});

  const dispatch = useDispatch();
  const selectedCodingForm = useRef<number>(0);

  useEffect(() => {
    const remote = assignment?.additionalPreferences;
    if (remote && typeof remote === 'object') {
      originalRef.current = {...remote};
      const initialLocal: Record<string, boolean> = {};
      Object.keys(labels).forEach((k) => {
        initialLocal[k] = Boolean((remote as Record<string, any>)[k]);
      });
      initialLocal.isEnableNativeRedactions = Boolean(
        (remote as Record<string, any>).isEnableImageRedactions
      );
      setLocalPrefs(initialLocal);
    }

    selectedCodingForm.current = JSON.parse(
      assignment.selectCodingFormLists ?? `[]`
    ).length;

    if (selectedCodingForm.current > 1) {
      handleToggleChange('shouldCompleteOnApplyCodingStamp', false);
    }
  }, [assignment]);

  const handleToggleChange = (key: string, value: boolean) => {
    setLocalPrefs((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'isEnableImageRedactions'
        ? {isEnableNativeRedactions: value}
        : {}),
    }));
  };

  const handleCancel = () => {
    if (!originalRef.current) return;
    const revert: Record<string, boolean> = {};
    Object.keys(labels).forEach((k) => {
      revert[k] = Boolean(originalRef.current![k]);
    });
    setLocalPrefs(revert);
    onClose();
  };
  const isEnableAllRedactions = REDACTION_KEYS.every(
    (key) => !!localPrefs[key]
  );

  const handleAllRedactionsToggle = (checked: boolean) => {
    setLocalPrefs((prev) => ({
      ...prev,
      isEnableImageRedactions: checked,
      isEnableAudioRedactions: checked,
      isEnableNativeRedactions: checked,
    }));
  };
  const handleSave = () => {
    if (!originalRef.current) return;
    const imageRedactions =
      localPrefs.isEnableImageRedactions ??
      Boolean(originalRef.current.isEnableImageRedactions);
    const mergedPayload = {
      ...originalRef.current,
      ...localPrefs,
      isEnableNativeRedactions: imageRedactions,
    };
    dispatch(setAdditionalPrefs(mergedPayload));
    onClose();
  };

  return (
    <Box className="manage-permission-main">
      <Box className="header">
        <Box className="header-title">
          <Typography
            className="typo-text headline3_size title-text"
            data-testid={`manage-permissions-title`}
          >
            {t('assignment.managePermissions.title')}
          </Typography>
          <ArrTooltipWrapper
            title={t('assignment.managePermissions.infoTitle')}
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            {t('assignment.managePermissions.infoDescription')}
          </ArrTooltipWrapper>
        </Box>
        <Box className="header-button">
          <ArrButton
            title={t('common.cancel')}
            dataTestId={'manage-permissions-common.cancel'}
            id={t('common.cancel')}
            name={t('common.cancel')}
            variant="secondary"
            size="small"
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            title={t('common.save')}
            dataTestId={'manage-common.save'}
            id={t('common.save')}
            name={t('common.save')}
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={isParentCascaseEnabled}
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      <Box className="body">
        {Object.entries(categories).map(([catKey, {left, right}], index) => (
          <Box key={catKey} className="groups">
            <Typography
              className="typo-text subhead3_size title-text"
              data-testid={`manage-permissions-cat-key-${index}`}
            >
              {t(`assignment.managePermissions.${catKey}`)}
            </Typography>
            <Box className="action-list">
              <Box className="toggle-col">
                {left.map((key) => (
                  <Box key={key} className="toggle-group">
                    <ArrToggle
                      disabled={isParentCascaseEnabled}
                      checked={!!localPrefs[key]}
                      onChange={(_, checked) =>
                        handleToggleChange(key, checked)
                      }
                      dataTestId={`arr-toggle-${key}`}
                      toggleSize="medium"
                    />
                    <Typography
                      className={`typo-text body3_size title-text ${isParentCascaseEnabled && 'disabled'}`}
                      data-testid={`manage-permissions-label-${index}`}
                    >
                      {labels[key] ?? key}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box className="toggle-col">
                {right.map((key) => (
                  <React.Fragment key={key}>
                    {key === 'isEnableImageRedactions' && (
                      <Box className={`toggle-group `}>
                        <ArrToggle
                          disabled={isParentCascaseEnabled}
                          checked={isEnableAllRedactions}
                          onChange={(_, checked) =>
                            handleAllRedactionsToggle(checked)
                          }
                          dataTestId="arr-toggle-isEnableAllRedactions"
                          toggleSize="medium"
                        />
                        <Typography
                          className={`typo-text body3_size title-text ${
                            isParentCascaseEnabled ? 'disabled' : ''
                          }`}
                        >
                          {t(
                            'assignment.managePermissions.permissionLabels.redactions'
                          )}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      className={`toggle-group ${
                        [
                          'isEnableImageRedactions',
                          'isEnableAudioRedactions',
                          // 'isEnableNativeRedactions',
                        ].includes(key)
                          ? 'redaction-child'
                          : ''
                      }`}
                    >
                      <ArrToggle
                        disabled={
                          isParentCascaseEnabled ||
                          (key === 'shouldCompleteOnApplyCodingStamp' &&
                            selectedCodingForm.current > 1)
                        }
                        checked={!!localPrefs[key]}
                        onChange={(_, checked) =>
                          handleToggleChange(key, checked)
                        }
                        dataTestId={`arr-toggle-${key}`}
                        toggleSize="medium"
                      />
                      <Typography
                        className={`typo-text body3_size title-text ${
                          isParentCascaseEnabled ||
                          (key === 'shouldCompleteOnApplyCodingStamp' &&
                            selectedCodingForm.current > 1)
                            ? 'disabled'
                            : ''
                        }`}
                      >
                        {labels[key] ?? key}
                      </Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ManagePermissions;
